import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { analyticsService } from '../../services/analytics.service';
import {
  ANALYTICS_MESSAGES,
  DEFAULT_RANGE_PRESET_ID,
} from '../../constants/analytics.constants';
import { PAGE_LAYOUT } from '../../constants/app.constants';
import {
  AnalyticsGranularity,
  AnalyticsOverview,
  TimelinePoint,
  UserContribution,
} from '../../types/analytics.types';
import { endOfTodayIso, isoDaysAgo } from '../../utils/analytics.utils';
import { findRangePreset, getDefaultGranularity } from '../../utils/analytics-range.utils';
import { AnalyticsHeader } from './components/AnalyticsHeader';
import { AnalyticsRangeRow } from './components/AnalyticsRangeRow';
import { AnalyticsTimelineChart } from './components/AnalyticsTimelineChart';
import { AnalyticsUserLeaderboard } from './components/AnalyticsUserLeaderboard';

export const AnalyticsPage: React.FC = () => {
  const [presetId, setPresetId] = useState<string>(DEFAULT_RANGE_PRESET_ID);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>(() => getDefaultGranularity());

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [users, setUsers] = useState<UserContribution[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const range = useMemo(() => {
    const preset = findRangePreset(presetId);
    return {
      from: isoDaysAgo(preset.days),
      to: endOfTodayIso(),
    };
  }, [presetId]);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const [overviewRes, timelineRes, usersRes] = await Promise.all([
        analyticsService.getOverview(range),
        analyticsService.getTimeline({ ...range, granularity }),
        analyticsService.getUserContributions(range),
      ]);
      setOverview(overviewRes);
      setTimeline(timelineRes);
      setUsers(usersRes);
    } catch (error) {
      const message = error instanceof Error ? error.message : ANALYTICS_MESSAGES.LOAD_FAILED;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [range, granularity]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handlePresetChange = (nextId: string): void => {
    const preset = findRangePreset(nextId);
    setPresetId(preset.id);
    setGranularity(preset.granularity);
  };

  return (
    <div className={PAGE_LAYOUT.PAGE_WRAPPER}>
      <div className={`${PAGE_LAYOUT.WIDE_CONTAINER_NO_VERTICAL} space-y-4`}>
        <AnalyticsHeader overview={overview} isLoading={isLoading} />

        <AnalyticsRangeRow
          activePresetId={presetId}
          onPresetChange={handlePresetChange}
          overview={overview}
          isLoading={isLoading}
          onRefresh={fetchAnalytics}
        />

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 min-w-0">
            <AnalyticsTimelineChart
              points={timeline}
              granularity={granularity}
              onGranularityChange={setGranularity}
              isLoading={isLoading}
            />
          </div>
          <div className="xl:col-span-1 min-w-0">
            <AnalyticsUserLeaderboard users={users} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
};
